import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useConfig } from '../../config/ConfigContext';
import { isMobileDevice } from '../../utils/deviceDetection';
import ShortsPlayerUnified from '../Shorts/ShortsPlayerUnified';
import ShortsNavigation from '../Shorts/ShortsNavigation';
import ShortsActions from '../Shorts/ShortsActions';
import ClipsInfo from './ClipsInfo';
import useClipsData from './hooks/useClipsData';
import useClipLikes from './hooks/useClipLikes';
import useShortsSwipe from '../Shorts/hooks/useShortsSwipe';
import useOrientation from '../../hooks/useOrientation';
import '../../styles/shorts.css';

/**
 * Lecture Clips Viewer — Drive-only player using the production Shorts layout.
 *
 * Mirrors ShortsViewer.js structure but simpler:
 * - Data from Supabase `lecture_clips` table (not YouTube/Sheets)
 * - ShortsPlayerUnified auto-detects Drive mode via `driveFileId`
 * - No YouTube-specific logic (no extractVideoId, no YT subscribe, no canAutoPlay)
 * - Composes same shared components: ShortsNavigation, ShortsActions, ShortsInfo
 */
const LectureClipsViewer = () => {
  const { config, loading: configLoading, theme } = useConfig();
  const navigate = useNavigate();
  const location = useLocation();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentSeries, setCurrentSeries] = useState('all');
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const [swipeDirection, setSwipeDirection] = useState(null);

  // Orientation via matchMedia API (real-time, reliable)
  const { isLandscape } = useOrientation();
  const [controlsVisible, setControlsVisible] = useState(true);
  const [showRotateHint, setShowRotateHint] = useState(false);

  // Data
  const {
    clips,
    series,
    loading: dataLoading,
    error: dataError,
    refetch
  } = useClipsData(currentSeries, config?.center_id);

  // Likes
  const currentClip = clips[currentIndex];
  const {
    likeCount,
    hasLiked,
    toggleLike
  } = useClipLikes(currentClip?.id);

  // Swipe + keyboard navigation
  const {
    swipeHandlers,
    containerRef
  } = useShortsSwipe({
    onSwipeUp: () => navigateToNext(),
    onSwipeDown: () => navigateToPrevious(),
    onSpacePress: () => setIsPlaying(prev => !prev),
    onMutePress: () => setIsMuted(prev => !prev)
  });

  // Navigation
  const navigateToNext = useCallback(() => {
    setSwipeDirection('up');
    setTimeout(() => setSwipeDirection(null), 300);
    if (currentIndex < clips.length - 1) {
      setCurrentIndex(prev => prev + 1);
    } else {
      setCurrentIndex(0); // Loop
    }
  }, [currentIndex, clips.length]);

  const navigateToPrevious = useCallback(() => {
    setSwipeDirection('down');
    setTimeout(() => setSwipeDirection(null), 300);
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    } else {
      setCurrentIndex(clips.length - 1); // Loop
    }
  }, [currentIndex, clips.length]);

  // Series change
  const handleSeriesChange = useCallback((seriesName) => {
    if (seriesName !== currentSeries) {
      setCurrentSeries(seriesName);
      setCurrentIndex(0);
    }
  }, [currentSeries]);

  // Controls visibility reacts to orientation (from useOrientation hook)
  useEffect(() => {
    if (isMobileDevice()) {
      setControlsVisible(!isLandscape);
    }
  }, [isLandscape]);

  // Rotate hint — reactive to THREE inputs: clip type, orientation, device
  // Shows ONLY when: mobile + portrait + landscape clip
  // Hides INSTANTLY when: clip changes to portrait, OR device rotates
  useEffect(() => {
    const clipType = currentClip?.clipType || 'shorts';
    const isLandscapeClip = clipType === 'landscape';
    const shouldShow = isMobileDevice() && !isLandscape && isLandscapeClip;

    if (!shouldShow) {
      setShowRotateHint(false);
      return;
    }

    // Brief delay so the hint doesn't flash during transitions
    const showTimer = setTimeout(() => setShowRotateHint(true), 800);
    // Auto-dismiss after 4 seconds
    const hideTimer = setTimeout(() => setShowRotateHint(false), 4800);

    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
    };
  }, [currentClip?.clipType, isLandscape, currentIndex]);

  // Auto-advance callback
  useEffect(() => {
    window.clipsAutoAdvance = navigateToNext;
    return () => { window.clipsAutoAdvance = null; };
  }, [navigateToNext]);

  // URL deep link: /lecture-clips?v={driveFileId}
  useEffect(() => {
    if (clips.length > 0) {
      const urlParams = new URLSearchParams(location.search);
      const driveParam = urlParams.get('v');
      if (driveParam) {
        const foundIndex = clips.findIndex(c => c.driveFileId === driveParam);
        if (foundIndex !== -1) setCurrentIndex(foundIndex);
      }
    }
  }, [clips, location.search]);

  // Ensure currentIndex is valid
  useEffect(() => {
    if (clips.length > 0 && currentIndex >= clips.length) {
      setCurrentIndex(0);
    }
  }, [clips.length, currentIndex]);

  // Share — captures video intro frame (2s) as image for rich preview
  const handleShare = useCallback(async (clip) => {
    if (config?.share_clips_enabled === false) return;

    const shareUrl = `${window.location.origin}/lecture-clips?v=${clip.driveFileId}`;
    const text = `Watch "${clip.title}" by ${clip.speakerName}`;

    // Try to capture the intro splash frame for a rich share image
    let shareFile = null;
    try {
      const videoEl = document.querySelector('.shorts-player-container video');
      if (videoEl && videoEl.readyState >= 2) {
        const savedTime = videoEl.currentTime;
        const wasPlaying = !videoEl.paused;
        if (wasPlaying) videoEl.pause();

        // Seek to 2s (intro splash fully rendered) and capture
        await new Promise((resolve) => {
          videoEl.onseeked = resolve;
          videoEl.currentTime = 2.0;
        });

        const canvas = document.createElement('canvas');
        canvas.width = videoEl.videoWidth;
        canvas.height = videoEl.videoHeight;
        canvas.getContext('2d').drawImage(videoEl, 0, 0);

        const blob = await new Promise(r => canvas.toBlob(r, 'image/png'));
        if (blob) {
          shareFile = new File([blob], `${clip.title || 'clip'}.png`, { type: 'image/png' });
        }

        // Restore playback position
        videoEl.currentTime = savedTime;
        if (wasPlaying) videoEl.play().catch(() => {});
      }
    } catch (_) {
      // Canvas capture failed — fall through to link-only share
    }

    // Share with image file if available, otherwise link-only
    if (navigator.share) {
      try {
        const shareData = { title: clip.title, text, url: shareUrl };
        if (shareFile && navigator.canShare?.({ files: [shareFile] })) {
          shareData.files = [shareFile];
        }
        await navigator.share(shareData);
      } catch (_) {}
    } else {
      try {
        await navigator.clipboard.writeText(shareUrl);
        alert('Link copied to clipboard!');
      } catch (_) {}
    }
  }, [config?.share_clips_enabled]);

  // Lecture link
  const handleLectureLink = useCallback((lectureId) => {
    if (lectureId) navigate(`/lecture/${lectureId}`);
  }, [navigate]);

  // Speaker link
  const handleSpeakerClick = useCallback((speakerName) => {
    if (speakerName) navigate(`/speaker/${encodeURIComponent(speakerName)}`);
  }, [navigate]);

  // ===== LOADING / ERROR STATES =====
  if (configLoading || dataLoading) {
    return (
      <div className="shorts-loading">
        <div className="loading-spinner"></div>
        <p>Loading lecture clips...</p>
      </div>
    );
  }

  if (!configLoading && !config?.lecture_clips_enabled) {
    navigate('/');
    return null;
  }

  if (dataError && clips.length === 0) {
    return (
      <div className="shorts-error">
        <h2>Unable to load lecture clips</h2>
        <p>{dataError}</p>
        <button onClick={refetch} className="retry-button">Try Again</button>
      </div>
    );
  }

  if (clips.length === 0) {
    return (
      <div className="shorts-empty">
        <h2>No lecture clips available</h2>
        <p>Check back later for new content!</p>
      </div>
    );
  }

  if (!currentClip) return null;

  const themeColor = config?.primaryColor || theme?.colors?.primary || '#1e4273';

  // Build shortData object for ShortsPlayerUnified
  // The player detects Drive mode from the presence of driveFileId
  const shortData = {
    driveFileId: currentClip.driveFileId,
    title: currentClip.title,
    series: currentClip.series,
    speakerName: currentClip.speakerName,
    lectureId: currentClip.lectureId,
  };

  return (
    <div
      className={`shorts-container ${isLandscape && isMobileDevice() ? 'mobile-landscape' : ''} ${controlsVisible ? 'controls-visible' : 'controls-hidden'}`}
      ref={containerRef}
      style={{ '--theme-color': themeColor }}
    >
      {/* Swipe detection layer */}
      <div
        className="swipe-detection-layer"
        {...swipeHandlers}
        onClick={() => {
          setIsPlaying(prev => !prev);
          if (window.toggleClipsInfo) window.toggleClipsInfo();
        }}
      ></div>

      {/* Rotate phone hint — shown once on mobile portrait */}
      {showRotateHint && (
        <div
          onClick={() => setShowRotateHint(false)}
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            zIndex: 100,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0,0,0,0.7)',
            backdropFilter: 'blur(4px)',
            animation: 'fadeIn 0.3s ease',
            cursor: 'pointer',
          }}
        >
          <div style={{
            animation: 'rotatePhone 1.5s ease-in-out infinite',
            fontSize: '48px',
            marginBottom: '16px',
          }}>
            📱
          </div>
          <p style={{
            color: 'white',
            fontSize: '16px',
            fontWeight: '600',
            textAlign: 'center',
            textShadow: '0 2px 8px rgba(0,0,0,0.5)',
          }}>
            Rotate for full screen
          </p>
          <p style={{
            color: 'rgba(255,255,255,0.6)',
            fontSize: '12px',
            marginTop: '8px',
          }}>
            Tap to dismiss
          </p>
          <style>{`
            @keyframes rotatePhone {
              0%, 100% { transform: rotate(0deg); }
              25% { transform: rotate(-30deg); }
              50% { transform: rotate(-90deg); }
              75% { transform: rotate(-30deg); }
            }
          `}</style>
        </div>
      )}

      {/* Top Navigation — series filter */}
      <ShortsNavigation
        series={series}
        currentSeries={currentSeries}
        shorts={clips}
        onSeriesChange={handleSeriesChange}
      />

      {/* Player — auto-detects Drive mode from driveFileId */}
      <ShortsPlayerUnified
        key={currentClip.driveFileId}
        shortData={shortData}
        isPlaying={isPlaying}
        isMuted={isMuted}
        onPlayStateChange={setIsPlaying}
        onMuteStateChange={setIsMuted}
        onNextVideo={navigateToNext}
        onPrevVideo={navigateToPrevious}
      />

      {/* Right side actions — no YouTube subscribe button */}
      <ShortsActions
        speakerName={currentClip.speakerName}
        likeCount={likeCount ?? currentClip.likeCount}
        hasLiked={hasLiked}
        onSpeakerClick={() => handleSpeakerClick(currentClip.speakerName)}
        onLikeClick={toggleLike}
        onShareClick={() => handleShare(currentClip)}
        onYouTubeClick={null}
        onLectureClick={() => handleLectureLink(currentClip.lectureId)}
        hasLectureLink={!!currentClip.lectureId}
        showYouTubeButton={false}
        shareEnabled={config?.share_clips_enabled !== false}
      />

      {/* Bottom info — with language badge and auto-hide */}
      <ClipsInfo
        speakerName={currentClip.speakerName}
        title={currentClip.title}
        publishedAt={currentClip.publishedAt}
        language={currentClip.language}
      />

      {/* Swipe feedback */}
      {swipeDirection && (
        <div className={`swipe-feedback swipe-${swipeDirection}`}>
          {swipeDirection === 'up' ? '⬆️' : '⬇️'}
        </div>
      )}

      {/* Pause indicator */}
      {!isPlaying && (
        <div className="pause-indicator">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="white" opacity="0.8">
            <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
          </svg>
        </div>
      )}
    </div>
  );
};

export default LectureClipsViewer;
